/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { numberToPercentageString, numberToString, truncateValue } from 'src/core/utils/number';
import { LabelOrDocumentDistributionPerDataSet } from 'src/customText/core/services/modelGuidance/customTextModelGuidanceSwaggers';
import { PerformanceMetricsBreakdown } from 'src/modules/modelDetails/stores/ModelEvaluationStore';

export type CustomPerformanceMetrics = PerformanceMetricsBreakdown & LabelOrDocumentDistributionPerDataSet;

export type ModelEvaluationTableProps = TableProps<CustomPerformanceMetrics> & {
    nameColumnTitle: string;
};

const messages = defineMessages({
    recall: { id: 'modelGuidance.modelEvaluationTable.recall', defaultMessage: 'Recall %' },
    f1Score: { id: 'modelGuidance.modelEvaluationTable.f1Score', defaultMessage: 'F1 score' },
    precision: { id: 'modelGuidance.modelEvaluationTable.precision', defaultMessage: 'Precision %' },
    numberOfTags: { id: 'modelGuidance.modelEvaluationTable.numberOfTags', defaultMessage: 'Number of labels' },
    trainingLabels: {
        id: 'modelGuidance.modelEvaluationTable.trainingLabels',
        defaultMessage: 'Training labels'
    },
    testingLabels: {
        id: 'modelGuidance.modelEvaluationTable.testingLabels',
        defaultMessage: 'Testing labels'
    }
});

export const ModelEvaluationTable = (props: ModelEvaluationTableProps) => {
    const { nameColumnTitle, items, sortKey, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 200,
            maxWidth: 350,
            isRowHeader: true,
            isResizable: true,
            fieldName: 'name',
            name: nameColumnTitle,
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc'
        },
        {
            minWidth: 100,
            maxWidth: 150,
            key: 'precision',
            isResizable: true,
            isRowHeader: false,
            fieldName: 'precision',
            name: localize(messages.precision),
            isSorted: sortKey?.key === 'precision',
            isSortedDescending: sortKey?.key === 'precision' && sortKey?.order === 'desc',
            onRender: (item: CustomPerformanceMetrics) => numberToPercentageString({ ratio: item.precision, showPercentage: false })
        },
        {
            key: 'recall',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            isRowHeader: false,
            fieldName: 'recall',
            name: localize(messages.recall),
            isSorted: sortKey?.key === 'recall',
            isSortedDescending: sortKey?.key === 'recall' && sortKey?.order === 'desc',
            onRender: (item: CustomPerformanceMetrics) => numberToPercentageString({ ratio: item.recall, showPercentage: false })
        },
        {
            key: 'f1Score',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            isRowHeader: false,
            fieldName: 'f1Score',
            name: localize(messages.f1Score),
            isSorted: sortKey?.key === 'f1Score',
            isSortedDescending: sortKey?.key === 'f1Score' && sortKey?.order === 'desc',
            onRender: (item: CustomPerformanceMetrics) => truncateValue(item.f1Score, 3)
        },
        {
            key: 'Train',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            isRowHeader: false,
            fieldName: 'Train',
            name: localize(messages.trainingLabels),
            isSorted: sortKey?.key === 'Train',
            onRender: (item: CustomPerformanceMetrics) => numberToString(item.Train),
            isSortedDescending: sortKey?.key === 'Train' && sortKey?.order === 'desc'
        },
        {
            key: 'Test',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            isRowHeader: false,
            fieldName: 'Test',
            isSorted: sortKey?.key === 'Test',
            name: localize(messages.testingLabels),
            onRender: (item: CustomPerformanceMetrics) => numberToString(item.Test),
            isSortedDescending: sortKey?.key === 'Test' && sortKey?.order === 'desc'
        }
    ];

    return <Table items={items} columns={columns} selectionMode={SelectionMode.none} {...rest} />;
};
